import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import PasswordInput from "@/components/ui/password-input";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const expired = search.get("expired") === "1";
  const next = search.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [busy, setBusy] = useState(false);

  // Se l'utente è già autenticato, redirect immediato.
  if (!loading && user) {
    return <Navigate to={next} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password, totp || undefined);
      toast.success("Bentornata!");
      nav(next);
    } catch (err) {
      if (err?.response?.status === 428) {
        setNeeds2fa(true);
        toast.info("Inserisci il codice 2FA");
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="/assets/studio-monopoli.jpg" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="hero-mask absolute inset-0" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.28em] opacity-80">Epic Flow Pilates</div>
          <div className="font-serif-display text-5xl mt-2 leading-tight">Bentornata<br/>nel tuo spazio.</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-md">
          <Link to="/" className="text-xs uppercase tracking-[0.28em] text-secondary hover:text-primary">← Torna al sito</Link>
          <h1 className="font-serif-display text-5xl mt-6">Accedi</h1>
          <p className="text-muted-foreground mt-2">Inserisci le tue credenziali per continuare.</p>

          {expired && (
            <div data-testid="login-expired-banner"
              className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="font-medium">Sessione scaduta</div>
              <div className="text-xs mt-0.5">Per sicurezza ti chiediamo di rientrare. Riprenderai da dove eri.</div>
            </div>
          )}

          <button type="button" data-testid="google-login-btn" onClick={startGoogleLogin}
            className="mt-8 w-full py-3 rounded-full border border-input bg-card hover:border-primary transition-colors flex items-center justify-center gap-3 text-sm">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continua con Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border"/>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">oppure</span>
            <div className="h-px flex-1 bg-border"/>
          </div>

          <label className="block text-xs uppercase tracking-widest text-secondary">Email</label>
          <input data-testid="login-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required
                 className="w-full mt-2 px-4 py-3 rounded-full border border-input bg-card focus:outline-none focus:border-primary" />

          <label className="block mt-4 text-xs uppercase tracking-widest text-secondary">Password</label>
          <PasswordInput
            data-testid="login-password"
            value={password}
            onChange={e=>setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full mt-2 px-4 py-3 rounded-full border border-input bg-card focus:outline-none focus:border-primary"
          />

          {needs2fa && (
            <>
              <label className="block mt-4 text-xs uppercase tracking-widest text-secondary">Codice 2FA (dall'app authenticator)</label>
              <input data-testid="login-totp" value={totp} onChange={e=>setTotp(e.target.value)} required maxLength={6}
                     placeholder="123456"
                     className="w-full mt-2 px-4 py-3 rounded-full border border-input bg-card focus:outline-none focus:border-primary tracking-widest text-center font-mono" />
            </>
          )}

          <button data-testid="login-submit" disabled={busy}
            className="mt-6 w-full py-4 rounded-full bg-primary text-primary-foreground btn-lift text-sm uppercase tracking-widest disabled:opacity-50">
            {busy ? "Accesso…" : "Entra"}
          </button>

          <div className="mt-3 text-right">
            <Link to="/password-dimenticata" className="text-xs text-secondary hover:text-primary underline underline-offset-4">Password dimenticata?</Link>
          </div>

          <div className="mt-6 text-sm text-muted-foreground">
            Non hai un account? <Link to={search.get("next") ? `/registrati?next=${encodeURIComponent(search.get("next"))}` : "/registrati"} className="text-primary hover:underline">Registrati</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
